import React from 'react';
import styled from 'styled-components';

// カスタムコンポーネントのProps型定義
export interface CustomComponentProps {
  problemId: string;
  currentImageIndex: number;
  imageUrls: string[];
  beforeImageUrls?: string[];
  onImageChange?: (index: number) => void;
  customProps?: Record<string, any>;
}

// サンプル: シンプルテキスト表示コンポーネント
const TextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 48px;
  font-weight: bold;
  color: #333;
  text-align: center;
  padding: 20px;
`;

export const SimpleTextComponent: React.FC<CustomComponentProps> = ({ customProps }) => {
  const text = customProps?.text || 'No text provided';
  const color = customProps?.color || '#333';
  
  return (
    <TextContainer style={{ color }}>
      {text}
    </TextContainer>
  );
};

// サンプル: アニメーションテキストコンポーネント
const AnimatedTextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 60px;
  font-weight: bold;
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.8; }
    100% { transform: scale(1); opacity: 1; }
  }
`;

export const AnimatedTextComponent: React.FC<CustomComponentProps> = ({ customProps }) => {
  const text = customProps?.text || 'Animated Text';
  const color = customProps?.color || '#dc143c';
  
  return (
    <AnimatedTextContainer style={{ color }}>
      {text}
    </AnimatedTextContainer>
  );
};

// サンプル: カウントダウンコンポーネント
const CountdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 120px;
  font-weight: bold;
  color: #dc143c;
`;

const CountdownLabel = styled.div`
  font-size: 36px;
  color: #666;
  margin-bottom: 20px;
`;

export const CountdownComponent: React.FC<CustomComponentProps> = ({ customProps }) => {
  const [count, setCount] = React.useState(customProps?.startCount || 10);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCount((prev: number) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <CountdownContainer>
      <CountdownLabel>{customProps?.label || 'Starting in...'}</CountdownLabel>
      {count}
    </CountdownContainer>
  );
};

// サンプル: グリッド画像表示コンポーネント
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 20px;
  height: 100%;
  align-items: center;
`;

const GridImage = styled.img<{ isActive: boolean }>`
  width: 100%;
  height: auto;
  max-height: 45vh;
  object-fit: contain;
  border: ${props => props.isActive ? '5px solid #dc143c' : '2px solid #ccc'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

export const GridImageComponent: React.FC<CustomComponentProps> = ({ 
  imageUrls, 
  currentImageIndex, 
  onImageChange 
}) => {
  if (!imageUrls || imageUrls.length === 0) {
    return <TextContainer>No images available</TextContainer>;
  }
  
  return (
    <GridContainer>
      {imageUrls.map((url, index) => (
        <GridImage
          key={index}
          src={url}
          isActive={index === currentImageIndex}
          onClick={() => onImageChange?.(index)}
          alt={`Image ${index + 1}`}
        />
      ))}
    </GridContainer>
  );
};

// 缶詰セット最終問題用コンポーネントをインポート
import { KanzumeLastProblemComponent } from './KanzumeLastProblem';

// カスタムコンポーネントレジストリ
export const customComponentRegistry: Record<string, React.FC<CustomComponentProps>> = {
  'simple-text': SimpleTextComponent,
  'animated-text': AnimatedTextComponent,
  'countdown': CountdownComponent,
  'grid-image': GridImageComponent,
  'kanzume-last-problem': KanzumeLastProblemComponent,
};

// カスタムコンポーネントを取得する関数
export const getCustomComponent = (type: string): React.FC<CustomComponentProps> | null => {
  return customComponentRegistry[type] || null;
};